/**
 * Accessibility utilities for BaseModal
 */

import {FOCUSABLE_ELEMENTS} from './constants.js';

let liveRegion = null;

/**
 * Get all focusable elements within a container
 * @param {HTMLElement} parent - Parent element
 * @returns {Array} Array of focusable elements
 */
export function getFocusableNodes(parent) {
    const nodes = parent.querySelectorAll(FOCUSABLE_ELEMENTS.join(','));
    return Array.from(nodes);
}

/**
 * Get or create ARIA live region
 * @returns {HTMLElement} Live region element
 * @private
 */
function getLiveRegion() {
    if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className     = 'sr-only';
        liveRegion.style.cssText = 'position:absolute; left:-10000px; width:1px; height:1px; overflow:hidden;';
        document.body.appendChild(liveRegion);
    }
    return liveRegion;
}

/**
 * Announce message to screen readers
 * @param {string} message - Message to announce
 */
export function announce(message) {
    const region       = getLiveRegion();
    region.textContent = '';

    setTimeout(() => {
        region.textContent = message;
    }, 100);
}

/**
 * Set focus to the first focusable element or specific element
 * @param {Object} modal - Modal instance
 * @param {string} [focusSelector] - CSS selector for specific focus target
 */
export function setFocusToFirstNode(modal, focusSelector = null) {
    // If disableFocus is true, don't set focus
    if (modal.config.disableFocus) return;

    let targetElement = null;

    if (focusSelector) {
        // Focus specific element if selector provided
        targetElement = modal.modal.querySelector(focusSelector);
    } else {
        // Otherwise focus first focusable element
        const focusableNodes = getFocusableNodes(modal.modal);
        if (focusableNodes.length > 0) {
            targetElement = focusableNodes[0];
        }
    }

    if (targetElement) {
        targetElement.focus();
    }
}

/**
 * Store last active element before opening modal
 * @param {Object} modal - Modal instance
 * @param {HTMLElement} element - Element to store
 */
export function storeLastFocus(modal, element) {
    modal._lastActiveElement = element;
}

/**
 * Restore last active element after closing modal
 * @param {Object} modal - Modal instance
 */
export function restoreLastFocus(modal) {
    const element = modal._lastActiveElement;
    if (!element || typeof element.focus !== 'function') {
        return;
    }

    // Restore focus immediately (not async)
    // This will automatically blur the current focused element
    element.focus();
}

/**
 * Clear focus from modal elements before closing
 * Prevents aria-hidden warning when focus is on element inside hidden modal
 * @param {Object} modal - Modal instance
 */
export function clearModalFocus(modal) {
    if (document.activeElement && modal.modal.contains(document.activeElement)) {
        // Remove focus from modal element
        document.activeElement.blur();

        // Force focus to document.body to ensure focus leaves the modal
        // This prevents aria-hidden warning during async operations
        document.body.focus({preventScroll:true});
    }
}

/**
 * Maintain focus within modal (trap focus)
 * @param {Object} modal - Modal instance
 * @param {KeyboardEvent} event - Keyboard event
 */
export function maintainFocus(modal, event) {
    if (!event) return;

    const focusableNodes = getFocusableNodes(modal.modal);

    // If there are no focusable elements, return
    if (focusableNodes.length === 0) return;

    // Get the first and last focusable elements
    const firstFocusableNode = focusableNodes[0];
    const lastFocusableNode  = focusableNodes[focusableNodes.length - 1];

    // Check if the Tab key is pressed
    if (event.key === 'Tab') {
        // If Shift + Tab is pressed and the first element is focused
        if (event.shiftKey && document.activeElement === firstFocusableNode) {
            lastFocusableNode.focus();
            event.preventDefault();
        }
        // If Tab is pressed and the last element is focused
        else if (!event.shiftKey && document.activeElement === lastFocusableNode) {
            firstFocusableNode.focus();
            event.preventDefault();
        }
    }
}
